import { useCallback, useState, useEffect } from "react";
import { FormikSelectField } from "src/components/FormikComponents/FormikWrappers";
import { ISelectFieldProps } from "src/components/FormikComponents/SelectField";
import { getBusinessLogicModifiers } from "src/redux/actions/CrewSchedule/CrewSchedule.Actions";
import { formatFunction, parseFunction } from "./helpers";
import { ILogicModifier } from "@vmosolutions/oc-interfaces";
import { Chip } from "@mui/material";
import { invertHexColor } from "src/pages/FlightTimeline/TimelineUtils";

interface ILogicModifierSelectFieldProps extends Omit<ISelectFieldProps, "options"> {
  mosaicPane: HTMLElement;
  valueKey?: string;
  includeInactive?: boolean;
}

/**
 * Specialized wrapper for "built-in" OC logic modifiers select field.
 * @param props -
 */
export const LogicModifierSelectField = (props: ILogicModifierSelectFieldProps): JSX.Element => {
  const { mosaicPane, label, name, onChange, isMulti, includeInactive = false, valueKey, ...selectProps } = props;

  const [logicModifiers, setLogicModifiers] = useState<ILogicModifier[]>([]);

  // Get the available logic modifiers upon render
  useEffect(() => {
    let isCancelled = false;
    const fetchLogicModifiers = async (): Promise<void> => {
      try {
        const data = await getBusinessLogicModifiers();
        if (!isCancelled) {
          let filteredData = data || [];

          // Filter out inactive logic modifiers unless explicitly included
          if (!includeInactive) {
            filteredData = filteredData.filter((lm) => lm.isActive);
          }

          // Sort by title
          filteredData = filteredData.sort((a, b) => a.title.toLowerCase().localeCompare(b.title.toLowerCase()));

          setLogicModifiers(filteredData);
        }
      } catch {
        if (!isCancelled) {
          setLogicModifiers([]);
        }
      }
    };
    void fetchLogicModifiers();
    return () => {
      isCancelled = true;
    };
  }, [includeInactive]);

  // Setup Format And Parse functions (used if valueKey is passed)
  const formatValue = useCallback(
    (value: string) => formatFunction(value, logicModifiers, valueKey, isMulti),
    [logicModifiers, valueKey, isMulti]
  );
  const parseOption = useCallback((option: string) => parseFunction(option, valueKey, isMulti), [valueKey, isMulti]);

  // Custom format for displaying logic modifiers with color chips
  const formatOptionLabel = useCallback((lm: ILogicModifier) => {
    return (
      <>
        <Chip
          label={lm.title}
          size="small"
          sx={{
            borderRadius: "3px",
            fontWeight: 900,
            fontSize: "0.8em",
            backgroundColor: `#${lm.color}`,
            color: invertHexColor(lm.color),
            marginRight: "5px",
          }}
        />
        {lm?.description}
      </>
    );
  }, []);

  const getOptionLabel = useCallback((lm: ILogicModifier) => `${lm.title} - ${lm.description}`, []);

  const getOptionValue = useCallback((lm: ILogicModifier) => lm._id, []);

  return (
    <FormikSelectField
      name={name}
      label={label ?? "Logic Modifier"}
      isMulti={isMulti}
      options={logicModifiers}
      format={(value) => (valueKey ? formatValue(value) : value)}
      parse={(option) => (valueKey ? parseOption(option) : option)}
      formatOptionLabel={formatOptionLabel}
      getOptionLabel={getOptionLabel}
      getOptionValue={getOptionValue}
      onChange={(value: any) => !!onChange && onChange(value)}
      menuPortalTarget={mosaicPane}
      menuPosition={mosaicPane ? "fixed" : undefined}
      placeholder={`Select Logic Modifier${isMulti ? "s" : ""}`}
      isSearchable={true}
      isClearable={true}
      {...selectProps}
    />
  );
};
